// src/pages/user/hooks/useUser.ts
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getApi } from '../../../api/api'
import { getSalesApi } from '../../../salesApi/salesApi'
import { ExecutorClusterResponse } from '../../../common/types/interfaces'
import { selectExecutorClusters, setExecutorClusters } from '../../../store/slices/executorSlice'
import { selectCurrentUser } from '../../../store/slices/currentUserSlice'
import { setCurrentCluster } from '../../../store/slices/currentClusterSlice'
import { TASK } from '../../../common/utils/constants'

export const useUser = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const clusters = useSelector(selectExecutorClusters)
  const currentUser = useSelector(selectCurrentUser)
  const [loading, setLoading] = useState(clusters.length === 0)
  const [statsModalActive, setStatsModalActive] = useState(false)
  const [detailsModalActive, setDetailsModalActive] = useState(false)
  const [settingsModalActive, setSettingsModalActive] = useState(false)
  const [selectedCluster, setSelectedCluster] = useState<ExecutorClusterResponse | null>(null)

  // ❌ настройки для модалки
  const [settings, setSettings] = useState<{
    isOnShift: boolean
    notifyNewTasks: boolean
  } | null>(null)
  const [settingsLoading, setSettingsLoading] = useState(false)

  const salesApi = getSalesApi()

  const fetchClusters = useCallback(async () => {
    if (!currentUser?.id) {
      setLoading(false)
      return
    }

    try {
      const api = getApi()
      const data = await api.getExecutorClusters()
      dispatch(setExecutorClusters(data))
    } catch (error) {
      console.error('Failed to fetch executor clusters:', error)
      if (clusters.length === 0) {
        dispatch(setExecutorClusters([]))
      }
    } finally {
      setLoading(false)
    }
  }, [dispatch, currentUser?.id, clusters.length])

  const handleStatsClick = useCallback((cluster: ExecutorClusterResponse) => {
    setSelectedCluster(cluster)
    setStatsModalActive(true)
  }, [])

  const handleDetailsClick = useCallback((cluster: ExecutorClusterResponse) => {
    setSelectedCluster(cluster)
    setDetailsModalActive(true)
  }, [])

  // ❌ сначала грузим настройки, потом открываем модалку
  const handleSettingsClick = useCallback(
    async (cluster: ExecutorClusterResponse) => {
      setSettingsLoading(true)
      setSettings(null)
      try {
        salesApi.setClusterUrl(cluster.cluster)
        const data = await salesApi.getExecutorSettings()
        setSettings(data)
        setSelectedCluster(cluster)
        setSettingsModalActive(true)
      } catch (error) {
        console.error('Failed to load settings:', error)
        // ❌ не открываем модалку
      } finally {
        setSettingsLoading(false)
      }
    },
    [salesApi],
  )

  const handleGoTo = useCallback(
    (cluster: ExecutorClusterResponse) => {
      localStorage.setItem('currentCluster', JSON.stringify(cluster.cluster))
      dispatch(setCurrentCluster(cluster.cluster))
      window.open(`https://${cluster.cluster.domain}`, '_blank')
    },
    [dispatch],
  )

  const closeStatsModal = useCallback(() => {
    setStatsModalActive(false)
    setSelectedCluster(null)
  }, [])

  const closeDetailsModal = useCallback(() => {
    setDetailsModalActive(false)
    setSelectedCluster(null)
  }, [])

  const closeSettingsModal = useCallback(() => {
    setSettingsModalActive(false)
    setSelectedCluster(null)
    setSettings(null)
  }, [])

  const goToTasks = useCallback(() => {
    navigate(TASK)
  }, [navigate])

  useEffect(() => {
    fetchClusters()
  }, [fetchClusters])

  return {
    clusters,
    loading,
    statsModalActive,
    detailsModalActive,
    settingsModalActive,
    selectedCluster,
    settings, // ❌ передать в модалку
    settingsLoading, // ❌ для индикации на кнопке, если надо
    handleStatsClick,
    handleDetailsClick,
    handleSettingsClick,
    handleGoTo,
    closeStatsModal,
    closeDetailsModal,
    closeSettingsModal,
    goToTasks,
    fetchClusters,
  }
}
